/**
 * PlatformHealthPage — Security / Platform Guardian view (Admin/Owner only).
 *
 * Consolidates server metrics, backup status, connection health,
 * and agent run health into one operational dashboard.
 *
 * Live-status vocabulary: source states use the shared classifier
 * (components/statusVocabulary.js — classifySource/isSourceStale) and the browser-offline signal
 * comes from components/useLiveState.js, so loading/fresh/stale/failed/offline mean here exactly
 * what they mean on the other live operator surfaces. A background refresh that failed while its
 * earlier payload is still shown renders as labelled stale (never silently current, never
 * fabricated "unknown"); only a source with no usable payload reads as unknown.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { serverApi, connectionsApi, agentRunsApi, backupGuardianApi, platformGuardianApi, knowledgeGapsApi, reportsApi } from "../services/api";
import { useNotification } from "../context/NotificationContext";
import { LIVE_STATUS_CLS, isSourceStale } from "../components/statusVocabulary";
import { classifySourceTruth } from "../components/sourceTruth";
import SourceTruthBadge from "../components/SourceTruthBadge";
import { runStatusLabel } from "../components/runStatusVocabulary";
import { useOnlineStatus } from "../components/useLiveState";

const FINDING_STATUS_TOAST = {
  acknowledged: "הממצא אושר",
  watching: "הממצא מסומן למעקב",
  resolved: "הממצא נסגר",
  ignored: "הממצא הוסתר",
  open: "הממצא נפתח מחדש",
};

const GAP_STATUS_TOAST = {
  ignored: "הפער הוסתר בהצלחה",
  acknowledged: "הפער סומן כמוכר",
  resolved: "הפער סומן כסגור",
  open: "הפער שוחזר לפתוח",
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("he-IL", { dateStyle: "short", timeStyle: "short" }).format(new Date(iso));
}

function fmtSize(mb) {
  if (mb == null) return "—";
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb.toFixed(0)} MB`;
}

// The one stale disclosure for every source on this page: a background refresh failed while
// react-query still serves the last successful payload. The payload stays visible, labelled as
// not-current — never laundered into "unknown" (which means nothing usable was ever loaded).
export function SourceStaleLabel({ stale, since }) {
  if (!stale) return null;
  const at = since > 0 ? fmtDate(new Date(since).toISOString()) : null;
  return (
    <p role="status" className="text-xs text-amber-700 font-medium">
      רענון נכשל — מוצג הנתון מהטעינה האחרונה שהצליחה{at ? ` (${at})` : ""}; ייתכן שהמצב אינו מעודכן.
    </p>
  );
}

// ─── OperationsPulseSection ───────────────────────────────────────────────────

function PulseChip({ label, value, color }) {
  return (
    <div className={`flex flex-col items-center justify-center rounded-lg border px-3 py-2 min-w-[70px] ${color}`}>
      <span className="text-xl font-bold leading-tight">{value}</span>
      <span className="text-xs opacity-80 text-center leading-tight mt-0.5">{label}</span>
    </div>
  );
}

function OperationsPulseSection() {
  const pulseQuery = useQuery({
    queryKey: ["ops-pulse"],
    queryFn: () => reportsApi.opsPulse(24),
    staleTime: 120000,
    refetchInterval: 300000,
  });
  const pulse = pulseQuery.data;

  if (pulseQuery.isLoading) return null;
  // Only a pulse that never loaded is unreadable; a failed refresh keeps the last good numbers,
  // shown below with the stale label instead of being replaced by an error card.
  if (!pulse) {
    return (
      <div className="col-span-full bg-white border rounded-xl shadow-sm px-5 py-4">
        <div role="alert" className="text-sm text-gray-600">לא ניתן לטעון את הדופק התפעולי</div>
      </div>
    );
  }

  const chips = [
    { label: "כרטיסים נפתחו", value: pulse.tickets.opened, color: "bg-blue-50 border-blue-200 text-blue-800" },
    { label: "כרטיסים נסגרו", value: pulse.tickets.closed ?? 0, color: "bg-green-50 border-green-200 text-green-800" },
    { label: "עדיפות גבוהה", value: pulse.tickets.open_high, color: pulse.tickets.open_high > 0 ? "bg-red-50 border-red-300 text-red-800" : "bg-gray-50 border-gray-200 text-gray-600" },
    { label: "ניתוחי AI", value: pulse.ai.runs, color: "bg-violet-50 border-violet-200 text-violet-800" },
    { label: "AI אושרו", value: pulse.ai.approved ?? 0, color: (pulse.ai.approved ?? 0) > 0 ? "bg-green-50 border-green-200 text-green-800" : "bg-gray-50 border-gray-200 text-gray-600" },
    { label: "AI נדחו", value: pulse.ai.rejected ?? 0, color: (pulse.ai.rejected ?? 0) > 0 ? "bg-red-50 border-red-300 text-red-800" : "bg-gray-50 border-gray-200 text-gray-600" },
    { label: "ממתינים לאישור", value: pulse.ai.pending_review, color: pulse.ai.pending_review > 0 ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-gray-50 border-gray-200 text-gray-600" },
    { label: "חסומים (חירום)", value: pulse.ai.crisis_blocked ?? 0, color: (pulse.ai.crisis_blocked ?? 0) > 0 ? `${LIVE_STATUS_CLS.blocked} font-semibold` : "bg-gray-50 border-gray-200 text-gray-600" },
    { label: "פערי ידע חדשים", value: pulse.knowledge_gaps.new, color: pulse.knowledge_gaps.new > 0 ? "bg-orange-50 border-orange-200 text-orange-800" : "bg-gray-50 border-gray-200 text-gray-600" },
    { label: "פערים לכתיבת מאמר", value: pulse.knowledge_gaps.needs_article ?? 0, color: (pulse.knowledge_gaps.needs_article ?? 0) > 0 ? "bg-purple-50 border-purple-300 text-purple-800" : "bg-gray-50 border-gray-200 text-gray-600" },
    { label: "ממצאי אבטחה", value: pulse.security_findings.open_total, color: pulse.security_findings.open_total > 0 ? "bg-red-50 border-red-200 text-red-700" : "bg-gray-50 border-gray-200 text-gray-600" },
    { label: "ממצאים חדשים", value: pulse.security_findings.new ?? 0, color: (pulse.security_findings.new ?? 0) > 0 ? "bg-orange-50 border-orange-300 text-orange-800" : "bg-gray-50 border-gray-200 text-gray-600" },
    { label: "ממצאים קריטיים", value: pulse.security_findings.critical ?? 0, color: (pulse.security_findings.critical ?? 0) > 0 ? "bg-red-100 border-red-400 text-red-900 font-semibold" : "bg-gray-50 border-gray-200 text-gray-600" },
    { label: "גילויים דחופים", value: pulse.discoveries.attention_needed, color: pulse.discoveries.attention_needed > 0 ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-gray-50 border-gray-200 text-gray-600" },
  ];

  return (
    <div className="col-span-full bg-white border rounded-xl shadow-sm px-5 py-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-gray-700">📡 דופק תפעולי — 24 שעות אחרונות</h2>
        <span className="text-xs text-gray-400">
          {new Intl.DateTimeFormat("he-IL", { timeStyle: "short" }).format(new Date(pulse.generated_at))}
        </span>
      </div>
      <SourceStaleLabel stale={isSourceStale(pulseQuery)} since={pulseQuery.dataUpdatedAt} />
      <div className="flex flex-wrap gap-2">
        {chips.map((c) => (
          <PulseChip key={c.label} label={c.label} value={c.value} color={c.color} />
        ))}
      </div>
    </div>
  );
}

// ─── HealthIndicator ──────────────────────────────────────────────────────────

function HealthIndicator({ ok, label, sublabel }) {
  const color = ok === true
    ? "border-green-200 bg-green-50"
    : ok === false
    ? "border-red-200 bg-red-50"
    : "border-gray-200 bg-gray-50";
  const dot = ok === true ? "bg-green-500" : ok === false ? "bg-red-500" : "bg-gray-400";

  return (
    <div className={`flex items-center gap-3 rounded-lg border p-3 ${color}`}>
      <div className={`h-2.5 w-2.5 rounded-full shrink-0 ${dot}`} />
      <div>
        <p className="text-sm font-medium text-gray-800">{label}</p>
        {sublabel && <p className="text-xs text-gray-500">{sublabel}</p>}
      </div>
    </div>
  );
}

// ─── SectionCard ─────────────────────────────────────────────────────────────

function SectionCard({ title, icon, children }) {
  return (
    <div className="bg-white rounded-xl border shadow-sm p-5 space-y-3">
      <h2 className="font-semibold text-gray-700 flex items-center gap-2">
        <span aria-hidden="true">{icon}</span> {title}
      </h2>
      {children}
    </div>
  );
}

// ─── ServerSection ────────────────────────────────────────────────────────────

function ServerSection({ data, failed, stale, since, onRefresh, refreshing }) {
  if (!data) {
    return failed ? (
      <SectionCard title="שרת Helpdesk" icon="🖥️">
        <div role="alert" className="text-sm text-gray-600">לא ניתן לטעון את נתוני השרת</div>
      </SectionCard>
    ) : null;
  }
  const disk = data.disk;
  const ram  = data.ram;
  const up   = data.uptime;

  return (
    <SectionCard title="שרת Helpdesk" icon="🖥️">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <HealthIndicator
          ok={disk ? disk.percent < 80 : null}
          label="דיסק"
          sublabel={disk ? `${disk.used_gb} GB / ${disk.total_gb} GB (${disk.percent}%)` : "לא זמין"}
        />
        <HealthIndicator
          ok={ram ? ram.percent < 85 : null}
          label="RAM"
          sublabel={ram ? `${ram.used_mb} MB / ${ram.total_mb} MB (${ram.percent}%)` : "לא זמין"}
        />
        <HealthIndicator
          ok={true}
          label="uptime"
          sublabel={up ? `${up.days} ימים, ${up.hours} שעות, ${up.minutes} דקות` : "לא זמין"}
        />
        <HealthIndicator
          ok={data.updates ? data.updates.count === 0 : null}
          label="עדכוני מערכת"
          sublabel={data.updates != null ? `${data.updates.count} עדכונים ממתינים` : "לא זמין"}
        />
      </div>
      <SourceStaleLabel stale={stale} since={since} />
      <div className="flex items-center gap-3 pt-1">
        <p className="text-xs text-gray-400">
          עודכן: {fmtDate(data.last_updated)}{data.cached ? " (מהמטמון)" : ""}
        </p>
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          className="text-xs text-brand-600 hover:underline disabled:opacity-50"
        >
          {refreshing ? "מרענן..." : "רענן"}
        </button>
      </div>
    </SectionCard>
  );
}

// ─── BackupSection ────────────────────────────────────────────────────────────

function BackupSection({ backups }) {
  if (!backups) return (
    <SectionCard title="גיבויים" icon="💾">
      <HealthIndicator ok={false} label="לא נמצאה תיקיית גיבוי" />
    </SectionCard>
  );

  const lastBackup = backups.last_backup ? new Date(backups.last_backup) : null;
  const hoursAgo = lastBackup ? (Date.now() - lastBackup.getTime()) / 3600000 : null;
  const backupOk = hoursAgo != null && hoursAgo < 26;

  return (
    <SectionCard title="גיבויים" icon="💾">
      <div className="space-y-2">
        <HealthIndicator
          ok={backupOk}
          label="גיבוי אחרון"
          sublabel={lastBackup ? fmtDate(backups.last_backup) : "לא נמצא גיבוי"}
        />
        <HealthIndicator
          ok={backups.count > 0}
          label={`${backups.count} קבצי גיבוי`}
          sublabel={`${fmtSize(backups.total_size_mb)} סה"כ`}
        />
      </div>
      {backups.files?.length > 0 && (
        <details className="text-xs text-gray-500">
          <summary className="cursor-pointer hover:text-gray-700 select-none">
            הצג קבצים ({backups.files.length})
          </summary>
          <ul className="mt-1 space-y-0.5 pl-2">
            {backups.files.slice(0, 5).map((f) => (
              <li key={f.name} className="font-mono">
                {f.name} — {fmtSize(f.size_mb)} — {fmtDate(f.date)}
              </li>
            ))}
            {backups.files.length > 5 && <li>...ועוד {backups.files.length - 5}</li>}
          </ul>
        </details>
      )}
    </SectionCard>
  );
}

// ─── BackupGuardianSection ────────────────────────────────────────────────────

const SEVERITY_STYLES = {
  ok:       "bg-green-50 border-green-200 text-green-700",
  warning:  "bg-amber-50  border-amber-200  text-amber-700",
  critical: "bg-red-50   border-red-200   text-red-700",
};

const SEVERITY_LABEL = { ok: "תקין", warning: "אזהרה", critical: "קריטי" };

function BackupGuardianSection() {
  const qc = useQueryClient();
  const [configOpen, setConfigOpen] = useState(false);
  const [cfg, setCfg] = useState(null);

  const healthQuery = useQuery({
    queryKey: ["backup-guardian-health"],
    queryFn: backupGuardianApi.health,
    refetchInterval: 60000,
  });
  const health = healthQuery.data;
  const healthLoading = healthQuery.isLoading;
  const healthFailed = healthQuery.isError;

  // (react-query 5 has no useQuery onSuccess: the old callback never ran, so `cfg` stayed null and the panel showed
  // made-up defaults; "Save" then wrote THOSE over the real configuration.)
  const { data: config } = useQuery({
    queryKey: ["backup-guardian-config"],
    queryFn: backupGuardianApi.getConfig,
  });

  const { addNotification: addBGNotification } = useNotification();
  const checkMutation = useMutation({
    mutationFn: backupGuardianApi.check,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["backup-guardian-health"] }),
    onError: (err) => addBGNotification(err?.response?.data?.detail || "שגיאה בבדיקת הגיבויים", "error"),
  });

  const configMutation = useMutation({
    mutationFn: backupGuardianApi.setConfig,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["backup-guardian-config"] });
      qc.invalidateQueries({ queryKey: ["backup-guardian-health"] });   // severity / max age were computed from the old config
      setCfg(null);
      setConfigOpen(false);
    },
    onError: (err) => addBGNotification(err?.response?.data?.detail || "שגיאה בשמירת הגדרות הגיבויים", "error"),
  });

  // null until the real configuration has loaded: never a made-up default that "Save" could write back
  const currentCfg = cfg ?? config ?? null;
  const editCfg = (patch) => setCfg((f) => ({ ...(f ?? config), ...patch }));

  const sev = health?.severity || "ok";
  const severityStyle = SEVERITY_STYLES[sev] || SEVERITY_STYLES.ok;

  return (
    <SectionCard title="שומר גיבויים" icon="🛡">
      {healthLoading ? (
        <div className="animate-pulse h-8 bg-gray-100 rounded" />
      ) : (
        <div className="space-y-3">
          {/* Status badge. A request that never loaded is "unknown", never the green "all backups
              healthy" default. A refresh that failed while the earlier health payload is retained
              is stale: the payload stays visible and the stale label says what it is. */}
          {healthFailed && !health ? (
            <div className="border rounded-lg px-3 py-2 text-sm font-medium bg-gray-50 border-gray-300 text-gray-700">
              לא ניתן לטעון את מצב הגיבויים — הסטטוס לא ידוע
            </div>
          ) : (
            <>
              <SourceStaleLabel stale={isSourceStale(healthQuery)} since={healthQuery.dataUpdatedAt} />
              <div className={`border rounded-lg px-3 py-2 text-sm font-medium ${severityStyle}`}>
                {SEVERITY_LABEL[sev]} — {health?.findings?.length ? health.findings[0] : "כל הגיבויים תקינים"}
              </div>

              {health?.findings?.length > 1 && (
                <ul className="text-xs text-red-600 list-disc pl-4 space-y-0.5">
                  {health.findings.slice(1).map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              )}

              <div className="text-xs text-gray-500 space-y-0.5">
                {health?.last_backup && <div>גיבוי אחרון: {fmtDate(health.last_backup)}</div>}
                {health?.age_hours != null && <div>גיל: {Math.round(health.age_hours)}ש</div>}
                {health?.checked_at && <div>נבדק: {fmtDate(health.checked_at)}</div>}
              </div>
            </>
          )}

          {checkMutation.data?.ticket_created && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              נפתחה קריאת אחזור #{checkMutation.data.ticket_number}
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => checkMutation.mutate()}
              disabled={checkMutation.isPending}
              className="text-xs border px-3 py-1 rounded hover:bg-gray-50 disabled:opacity-50"
            >
              {checkMutation.isPending ? "בודק..." : "בדוק עכשיו"}
            </button>
            <button
              onClick={() => { setConfigOpen(!configOpen); setCfg(currentCfg); }}
              className="text-xs text-brand-600 hover:underline"
            >
              הגדרות
            </button>
          </div>

          {/* Inline config panel */}
          {configOpen && !currentCfg && (
            <div role="alert" className="border rounded-lg p-3 bg-red-50 border-red-200 text-red-700 text-xs">
              לא ניתן לטעון את הגדרות שומר הגיבויים — לא ניתן לערוך אותן כרגע
            </div>
          )}
          {configOpen && currentCfg && (
            <div className="border rounded-lg p-3 bg-gray-50 space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">גיל מקסימלי (שעות)</label>
                  <input
                    type="number" min="1" max="720"
                    value={currentCfg.max_age_hours}
                    onChange={(e) => editCfg({ max_age_hours: parseInt(e.target.value, 10) || 25 })}
                    className="w-full border rounded px-2 py-1 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">מינ' קבצים</label>
                  <input
                    type="number" min="0"
                    value={currentCfg.min_count}
                    onChange={(e) => editCfg({ min_count: parseInt(e.target.value, 10) || 0 })}
                    className="w-full border rounded px-2 py-1 text-xs"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox" id="auto-ticket"
                  checked={currentCfg.auto_ticket}
                  onChange={(e) => editCfg({ auto_ticket: e.target.checked })}
                  className="rounded"
                />
                <label htmlFor="auto-ticket" className="text-xs text-gray-700">
                  צור קריאה אוטומטית בעת בעיה
                </label>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => configMutation.mutate(currentCfg)}
                  disabled={configMutation.isPending}
                  className="text-xs bg-brand-600 text-white px-3 py-1 rounded disabled:opacity-50"
                >
                  שמור
                </button>
                <button onClick={() => setConfigOpen(false)} className="text-xs border px-3 py-1 rounded hover:bg-gray-50">
                  ביטול
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
}

// ─── PlatformGuardianSection ──────────────────────────────────────────────────

const RUN_STATUS_STYLES = {
  pending:                     "text-blue-600",
  analyzing:                   "text-blue-600",
  awaiting_technician_review:  "text-amber-600",
  awaiting_approval:           "text-amber-600",
  completed:                   "text-green-600",
  resolved:                    "text-green-600",
  failed:                      "text-red-600",
  rejected:                    "text-red-600",
};

function PlatformGuardianSection() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [runMsg, setRunMsg] = useState(null);

  const statusQuery = useQuery({
    queryKey: ["platform-guardian-status"],
    queryFn: platformGuardianApi.status,
    refetchInterval: 30000,
  });
  const statusData = statusQuery.data;
  const statusLoading = statusQuery.isLoading;
  const statusFailed = statusQuery.isError;

  const runMutation = useMutation({
    mutationFn: platformGuardianApi.run,
    onSuccess: (res) => {
      setRunMsg(`סריקה התחילה (ריצה ${res.run_id.slice(0, 8)}…)`);
      qc.invalidateQueries({ queryKey: ["platform-guardian-status"] });
      if (res.run_id) navigate(`/agent-runs/${res.run_id}`);
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      setRunMsg(typeof detail === "string" && detail ? detail : "שגיאה בהפעלת הסריקה");
    },
  });

  const lastStatus = statusData?.last_run_status;
  const statusStyle = RUN_STATUS_STYLES[lastStatus] || "text-gray-600";

  return (
    <SectionCard title="שומר פלטפורמה" icon="🔒">
      {statusLoading ? (
        <div className="animate-pulse h-8 bg-gray-100 rounded" />
      ) : (
        <div className="space-y-3">
          <SourceStaleLabel stale={isSourceStale(statusQuery)} since={statusQuery.dataUpdatedAt} />
          <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
            <div>
              <span className="text-gray-400">סוכן: </span>
              {statusFailed && !statusData ? (
                // a failed request is not "disabled": the state is unknown
                <span role="alert" className="text-gray-600">לא ידוע (הטעינה נכשלה)</span>
              ) : (
                <span className={statusData?.agent_enabled ? "text-green-600" : "text-red-600"}>
                  {statusData?.agent_enabled ? "פעיל" : "מושבת"}
                </span>
              )}
            </div>
            {statusData?.last_run_status && (
              <div>
                <span className="text-gray-400">ריצה אחרונה: </span>
                <span className={statusStyle}>{runStatusLabel(statusData.last_run_status)}</span>
              </div>
            )}
            {statusData?.last_run_at && (
              <div className="col-span-2">
                <span className="text-gray-400">בוצע: </span>
                {fmtDate(statusData.last_run_at)}
              </div>
            )}
            {!statusData?.last_run_at && !statusFailed && (
              <div className="col-span-2 text-gray-400">טרם בוצעה סריקה</div>
            )}
          </div>

          {runMsg && (
            <div className="text-xs text-brand-700 bg-brand-50 border border-brand-200 rounded px-2 py-1">
              {runMsg}
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => { setRunMsg(null); runMutation.mutate(); }}
              disabled={runMutation.isPending || !statusData?.agent_enabled}
              className="text-xs bg-brand-600 hover:bg-brand-700 text-white px-3 py-1.5 rounded font-medium disabled:opacity-50 transition-colors"
            >
              {runMutation.isPending ? "מפעיל..." : "הפעל סריקת אבטחה"}
            </button>
            {statusData?.last_run_id && (
              <Link
                to={`/agent-runs/${statusData.last_run_id}`}
                className="text-xs text-brand-600 hover:underline self-center"
              >
                ראה תוצאה ←
              </Link>
            )}
          </div>
        </div>
      )}
    </SectionCard>
  );
}

// ─── PostureFindingsSection ───────────────────────────────────────────────────

const SEVERITY_BADGE = {
  critical: "bg-red-100 text-red-700 border-red-300",
  high:     "bg-orange-100 text-orange-700 border-orange-300",
  medium:   "bg-yellow-100 text-yellow-700 border-yellow-300",
  low:      "bg-blue-100 text-blue-700 border-blue-300",
};
const SEVERITY_HE = {
  critical: "קריטי",
  high:     "גבוה",
  medium:   "בינוני",
  low:      "נמוך",
  info:     "מידע",
};
const STATUS_BADGE_F = {
  open:         "bg-red-50 text-red-700",
  acknowledged: "bg-amber-50 text-amber-700",
  watching:     "bg-blue-50 text-blue-700",
  resolved:     "bg-green-50 text-green-700",
  ignored:      "bg-gray-100 text-gray-400",
};
const STATUS_HE_F = {
  open:         "פתוח",
  acknowledged: "אושר",
  watching:     "במעקב",
  resolved:     "נפתר",
  ignored:      "מוסתר",
};

export function PostureFindingRow({ finding, onStatus, onCreateTicket, creatingTicket, isUpdatingStatus = false }) {
  return (
    <div className={`border rounded-lg p-3 space-y-1 ${finding.status === "ignored" || finding.status === "resolved" ? "opacity-50" : ""}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${SEVERITY_BADGE[finding.severity] || SEVERITY_BADGE.medium}`}>
          {SEVERITY_HE[finding.severity] ?? finding.severity}
        </span>
        <span className="text-sm font-medium text-gray-800">{finding.title}</span>
        <span className={`text-xs px-2 py-0.5 rounded-full ml-auto ${STATUS_BADGE_F[finding.status] || ""}`}>
          {STATUS_HE_F[finding.status] || finding.status}
        </span>
      </div>
      {finding.summary && <p className="text-xs text-gray-600">{finding.summary}</p>}
      {finding.recommendation && (
        <p className="text-xs text-brand-700 bg-brand-50 border border-brand-100 rounded px-2 py-1">
          💡 {finding.recommendation}
        </p>
      )}
      {finding.evidence && Object.keys(finding.evidence).length > 0 && (
        <details className="text-xs text-gray-500 border-t border-gray-100 pt-1">
          <summary className="cursor-pointer hover:text-gray-700">פרטי ראיות</summary>
          <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap rounded bg-gray-50 p-2 text-[11px] text-gray-600">
            {JSON.stringify(finding.evidence, null, 2)}
          </pre>
        </details>
      )}
      {finding.resolved_by && (
        <p className="text-xs text-green-600">נסגר על ידי {finding.resolved_by}</p>
      )}
      {finding.category && (
        <span className="text-[10px] text-gray-400 border border-gray-100 rounded px-1.5 py-0.5 bg-gray-50 w-fit">
          {finding.category}
        </span>
      )}
      <div className="flex items-center gap-2 text-xs text-gray-400 flex-wrap pt-0.5">
        <span>ראשון: {fmtDate(finding.first_seen_at)}</span>
        {finding.last_seen_at && finding.last_seen_at !== finding.first_seen_at && (
          <>
            <span>•</span>
            <span>אחרון: {fmtDate(finding.last_seen_at)}</span>
          </>
        )}
        <span>•</span>
        <span>פעמים: {finding.occurrence_count}</span>
        {finding.source_run_id && (
          <Link to={`/agent-runs/${finding.source_run_id}`} className="text-brand-500 hover:underline">ריצה ←</Link>
        )}
        <span className="mr-auto flex gap-1">
          {finding.status === "open" && (
            <>
              <button onClick={() => onStatus(finding.id, "acknowledged")} disabled={isUpdatingStatus} className="px-2 py-0.5 border rounded hover:bg-amber-50 text-amber-700 border-amber-200 disabled:opacity-50">אשר</button>
              <button onClick={() => onStatus(finding.id, "watching")} disabled={isUpdatingStatus} className="px-2 py-0.5 border rounded hover:bg-blue-50 text-blue-700 border-blue-200 disabled:opacity-50">עקוב</button>
            </>
          )}
          {finding.status !== "resolved" && finding.status !== "ignored" && (
            <button onClick={() => onStatus(finding.id, "resolved")} disabled={isUpdatingStatus} className="px-2 py-0.5 border rounded hover:bg-green-50 text-green-700 border-green-200 disabled:opacity-50">סגור</button>
          )}
          {finding.status !== "ignored" && (
            <button onClick={() => onStatus(finding.id, "ignored")} disabled={isUpdatingStatus} className="px-2 py-0.5 border rounded hover:bg-gray-100 text-gray-500 border-gray-200 disabled:opacity-50">הסתר</button>
          )}
          {(finding.status === "resolved" || finding.status === "ignored") && (
            <button onClick={() => onStatus(finding.id, "open")} disabled={isUpdatingStatus} className="px-2 py-0.5 border rounded hover:bg-red-50 text-red-700 border-red-200 disabled:opacity-50">פתח מחדש</button>
          )}
          {onCreateTicket && (
            <button
              onClick={() => onCreateTicket(finding.id)}
              disabled={creatingTicket === finding.id}
              className="px-2 py-0.5 border rounded hover:bg-violet-50 text-violet-700 border-violet-200 disabled:opacity-50"
              title="צור קריאה מממצא זה"
            >
              {creatingTicket === finding.id ? "יוצר..." : "צור קריאה"}
            </button>
          )}
        </span>
      </div>
    </div>
  );
}

// ignored (the row's own "הסתר" button sets this) had no tab of its own: a hidden finding was
// only visible again mixed into "הכל" (All) with every other status, unlike the identical pattern
// TechScoutPage.jsx/KnowledgePage.jsx already implement (an ignored/rejected tab of its own).
const FINDING_STATUS_TABS = [
  { key: "open",         label: "פתוח",    cls: "text-red-700 border-red-400 bg-red-50" },
  { key: "acknowledged", label: "אושר",    cls: "text-amber-700 border-amber-400 bg-amber-50" },
  { key: "watching",     label: "במעקב",   cls: "text-blue-700 border-blue-400 bg-blue-50" },
  { key: "resolved",     label: "נסגר",    cls: "text-green-700 border-green-400 bg-green-50" },
  { key: "ignored",      label: "מוסתר",   cls: "text-gray-500 border-gray-400 bg-gray-100" },
  { key: "all",          label: "הכל",     cls: "text-gray-700 border-gray-300 bg-white" },
];

function PostureFindingsSection() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const [sortBy, setSortBy] = useState("severity");
  const [statusTab, setStatusTab] = useState("open");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [creatingTicket, setCreatingTicket] = useState(null);

  const findingsQuery = useQuery({
    queryKey: ["posture-findings", sortBy, statusTab, severityFilter],
    queryFn: () => platformGuardianApi.findings({
      sort_by: sortBy,
      status_filter: statusTab === "all" ? undefined : statusTab,
      severity: severityFilter === "all" ? undefined : severityFilter,
    }),
    refetchInterval: 60000,
  });
  const findings = findingsQuery.data ?? [];
  const findingsLoading = findingsQuery.isLoading;
  const findingsFailed = findingsQuery.isError;

  const summaryQuery = useQuery({
    queryKey: ["posture-findings-summary"],
    queryFn: platformGuardianApi.findingsSummary,
    refetchInterval: 60000,
  });
  const summary = summaryQuery.data;

  const statusMutation = useMutation({
    mutationFn: ({ id, s }) => platformGuardianApi.updateStatus(id, s),
    onSuccess: (_, { s }) => {
      const msg = FINDING_STATUS_TOAST[s];
      if (msg) addNotification(msg, "info");
      qc.invalidateQueries({ queryKey: ["posture-findings"] });
      qc.invalidateQueries({ queryKey: ["posture-findings-summary"] });   // a different first key element: the prefix above does not match it
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בעדכון ממצא", "error"),
  });

  const handleCreateTicket = async (findingId) => {
    setCreatingTicket(findingId);
    try {
      const result = await platformGuardianApi.createTicket(findingId);
      if (result.existing) addNotification(`כבר קיימת קריאה פתוחה לממצא זה (#${result.ticket_number})`, "info");
      navigate(`/tickets/${result.ticket_id}`);
    } catch (err) {
      // This used to swallow the failure: the button just reset and nothing said why.
      addNotification(err?.response?.data?.detail || "שגיאה ביצירת הקריאה", "error");
      setCreatingTicket(null);
    }
  };

  if (findingsLoading) {
    return <SectionCard title="ממצאי אבטחה" icon="🛡️"><div className="animate-pulse h-8 bg-gray-100 rounded" /></SectionCard>;
  }

  return (
    <SectionCard title={`ממצאי אבטחה${findings.length ? ` (${findings.length})` : ""}`} icon="🛡️">
      {summary && (
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="bg-red-50 border border-red-200 text-red-700 rounded px-2 py-1">
            פתוחים: <strong>{summary.open_total}</strong>
          </span>
          <span className="bg-red-100 border border-red-300 text-red-800 rounded px-2 py-1">
            קריטיים: <strong>{summary.open_critical}</strong>
          </span>
          <span className="bg-gray-50 border border-gray-200 text-gray-600 rounded px-2 py-1">
            סה"כ: <strong>{summary.total}</strong>
          </span>
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex gap-1 text-xs flex-wrap">
          {FINDING_STATUS_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setStatusTab(t.key)}
              className={`px-2 py-1 rounded-full border transition-colors ${
                statusTab === t.key ? t.cls + " font-medium" : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs" aria-label="סינון לפי חומרה">
          {[{ key: "all", label: "כל החומרות" }, { key: "critical", label: "קריטי" }, { key: "high", label: "גבוה" }, { key: "medium", label: "בינוני" }, { key: "low", label: "נמוך" }].map((t, index) => (
            <button
              key={t.key}
              onClick={() => setSeverityFilter(t.key)}
              className={`px-2 py-1.5 ${index > 0 ? "border-r border-gray-200" : ""} ${severityFilter === t.key ? "bg-gray-800 text-white font-medium" : "bg-white text-gray-600 hover:bg-gray-50"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
          <button
            onClick={() => setSortBy("severity")}
            className={`px-3 py-1.5 ${sortBy === "severity" ? "bg-red-600 text-white font-medium" : "bg-white text-gray-600 hover:bg-gray-50"}`}
          >
            קריטיות ראשון
          </button>
          <button
            onClick={() => setSortBy("occurrences")}
            className={`px-3 py-1.5 border-r border-l border-gray-200 ${sortBy === "occurrences" ? "bg-amber-600 text-white font-medium" : "bg-white text-gray-600 hover:bg-gray-50"}`}
          >
            חוזר ביותר
          </button>
          <button
            onClick={() => setSortBy("recent")}
            className={`px-3 py-1.5 border-r border-gray-200 ${sortBy === "recent" ? "bg-brand-600 text-white font-medium" : "bg-white text-gray-600 hover:bg-gray-50"}`}
          >
            חדש
          </button>
        </div>
      </div>
      <SourceStaleLabel
        stale={isSourceStale(findingsQuery) || isSourceStale(summaryQuery)}
        since={findingsQuery.dataUpdatedAt}
      />
      {findingsFailed && findings.length === 0 ? (
        <p role="alert" className="text-sm text-red-600">לא ניתן לטעון את ממצאי האבטחה — המצב אינו ידוע</p>
      ) : findings.length === 0 ? (
        <p className="text-sm text-gray-400">אין ממצאים בקטגוריה זו</p>
      ) : (
        <div className="space-y-2">
          {findings.map((f) => (
            <PostureFindingRow
              key={f.id}
              finding={f}
              onStatus={(id, s) => statusMutation.mutate({ id, s })}
              onCreateTicket={handleCreateTicket}
              creatingTicket={creatingTicket}
              isUpdatingStatus={statusMutation.isPending && statusMutation.variables?.id === f.id}
            />
          ))}
        </div>
      )}
    </SectionCard>
  );
}

// ─── KnowledgeGapsSection ─────────────────────────────────────────────────────

const GAP_CATEGORY_HE = {
  auth:       "אימות",
  network:    "רשת",
  email:      "אימייל",
  hardware:   "חומרה",
  software:   "תוכנה",
  sharepoint: "SharePoint",
  security:   "אבטחה",
  general:    "כללי",
};

const GAP_STATUS_BADGE = {
  open:         "bg-red-50 text-red-700 border-red-200",
  acknowledged: "bg-amber-50 text-amber-700 border-amber-200",
  resolved:     "bg-green-50 text-green-700 border-green-200",
};

const GAP_STATUS_HE = {
  open:         "פתוח",
  acknowledged: "ידוע",
  resolved:     "נפתר",
};

function KnowledgeGapsSection() {
  const qc = useQueryClient();
  const { addNotification } = useNotification();
  const [activeFilter, setActiveFilter] = useState("open");
  const [showAll, setShowAll] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("");

  const gapsQuery = useQuery({
    queryKey: ["knowledge-gaps", activeFilter, categoryFilter],
    queryFn: () =>
      activeFilter === "needs_article"
        ? knowledgeGapsApi.listNeedsArticle()
        : knowledgeGapsApi.list({ status_filter: activeFilter, category: categoryFilter || undefined }),
    staleTime: 60000,
  });
  const gaps = gapsQuery.data ?? [];
  const gapsLoading = gapsQuery.isLoading;
  const gapsFailed = gapsQuery.isError;

  // The "X open" badge must reflect ALL open gaps, not just the currently active tab's list: `gaps`
  // above is server-filtered by activeFilter, so counting "open" rows inside it collapses to 0 on
  // every tab except "open" itself (and needs_article, which isn't status-filtered at all) -- a
  // technician on the "acknowledged"/"resolved" tab would see no badge even with real open gaps
  // still needing triage. Independent query, same pattern PostureFindingsSection uses for its badges.
  const openGapsQuery = useQuery({
    queryKey: ["knowledge-gaps-open-count", categoryFilter],
    queryFn: () => knowledgeGapsApi.list({ status_filter: "open", category: categoryFilter || undefined }),
    staleTime: 60000,
  });
  const openGapsForBadge = openGapsQuery.data ?? [];

  const statusMutation = useMutation({
    mutationFn: ({ id, status }) => knowledgeGapsApi.updateStatus(id, status),
    onSuccess: (_, variables) => {
      const msg = GAP_STATUS_TOAST[variables.status];
      if (msg) addNotification(msg, "info");
      qc.invalidateQueries({ queryKey: ["knowledge-gaps"] });
      qc.invalidateQueries({ queryKey: ["knowledge-gaps-open-count"] });   // a different first key element: the prefix above does not match it
      // Same reason: KnowledgePage.jsx's own "recurring gaps" panel reads this separate key and
      // would otherwise keep showing this gap as still needing an article for up to its 2-minute
      // staleTime after it's been acknowledged/resolved here.
      qc.invalidateQueries({ queryKey: ["knowledge-gaps-needs-article"] });
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בעדכון סטטוס הפער", "error"),
  });

  const [articleCreatedFor, setArticleCreatedFor] = useState(null);
  const createArticleMutation = useMutation({
    mutationFn: (id) => knowledgeGapsApi.createArticle(id),
    onSuccess: (_data, id) => {
      setArticleCreatedFor(id);
      // Creating an article from gap A then gap B within 4s used to have A's timer clear B's
      // still-fresh checkmark early -- only clear if this id is still the one showing.
      setTimeout(() => setArticleCreatedFor((cur) => (cur === id ? null : cur)), 4000);
      qc.invalidateQueries({ queryKey: ["knowledge"] });
      qc.invalidateQueries({ queryKey: ["knowledge-gaps"] });
      qc.invalidateQueries({ queryKey: ["knowledge-gaps-open-count"] });
      qc.invalidateQueries({ queryKey: ["knowledge-gaps-needs-article"] });
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה ביצירת מאמר מהפער", "error"),
  });

  const visible = showAll ? gaps : gaps.slice(0, 5);
  const openCount = openGapsForBadge.length;

  return (
    <div className="bg-white border rounded-xl shadow-sm p-5 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <h2 className="font-semibold text-gray-700">📚 פערי ידע מ-AI</h2>
          {openCount > 0 && (
            <span className="text-xs bg-red-100 text-red-700 border border-red-200 rounded-full px-2 py-0.5">
              {openCount} פתוחים
            </span>
          )}
        </div>
        <div className="flex gap-1 text-xs flex-wrap">
          {["open", "acknowledged", "resolved"].map((s) => (
            <button
              key={s}
              onClick={() => { setActiveFilter(s); setShowAll(false); }}
              className={`px-2 py-1 rounded-full border transition-colors ${
                activeFilter === s
                  ? "bg-brand-600 text-white border-brand-600"
                  : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
              }`}
            >
              {GAP_STATUS_HE[s]}
            </button>
          ))}
          <button
            onClick={() => { setActiveFilter("needs_article"); setShowAll(false); }}
            className={`px-2 py-1 rounded-full border transition-colors ${
              activeFilter === "needs_article"
                ? "bg-purple-600 text-white border-purple-600"
                : "bg-white text-purple-600 border-purple-200 hover:border-purple-400"
            }`}
          >
            📝 צריך מאמר
          </button>
        </div>
      </div>

      {activeFilter !== "needs_article" && (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-gray-500">קטגוריה:</span>
          <div className="flex gap-1 flex-wrap">
            <button
              onClick={() => setCategoryFilter("")}
              className={`px-2 py-0.5 rounded-full border transition-colors ${
                !categoryFilter ? "bg-gray-700 text-white border-gray-700" : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
              }`}
            >
              הכל
            </button>
            {Object.entries(GAP_CATEGORY_HE).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setCategoryFilter(categoryFilter === key ? "" : key)}
                className={`px-2 py-0.5 rounded-full border transition-colors ${
                  categoryFilter === key ? "bg-brand-600 text-white border-brand-600" : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      <SourceStaleLabel
        stale={isSourceStale(gapsQuery) || isSourceStale(openGapsQuery)}
        since={gapsQuery.dataUpdatedAt}
      />

      {gapsLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => <div key={i} className="animate-pulse h-10 bg-gray-100 rounded" />)}
        </div>
      ) : gapsFailed && visible.length === 0 ? (
        <p role="alert" className="text-sm text-red-600 text-center py-3">לא ניתן לטעון את פערי הידע</p>
      ) : visible.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-3">אין פערי ידע בקטגוריה זו</p>
      ) : (
        <div className="space-y-2">
          {visible.map((g) => (
            <div key={g.id} className="flex items-start gap-3 border border-gray-100 rounded-lg px-3 py-2 bg-gray-50">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs px-1.5 py-0.5 rounded border ${GAP_STATUS_BADGE[g.status] || GAP_STATUS_BADGE.open}`}>
                    {GAP_STATUS_HE[g.status] || g.status}
                  </span>
                  <span className="text-xs text-gray-400 border border-gray-200 rounded-full px-1.5 py-0.5">
                    {GAP_CATEGORY_HE[g.category] || g.category}
                  </span>
                  <span className="text-xs text-gray-400">×{g.occurrence_count}</span>
                </div>
                <p className="text-sm text-gray-700 mt-0.5 truncate" title={g.gap_text}>{g.gap_text}</p>
                {g.acknowledged_by && (
                  <p className="text-xs text-amber-600 mt-0.5">ידוע על ידי {g.acknowledged_by} · {fmtDate(g.acknowledged_at)}</p>
                )}
                {g.source_tickets?.length > 0 && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    קריאות:{" "}
                    {g.source_tickets.slice(0, 3).map((t, i) => (
                      <span key={t.id}>
                        {i > 0 && ", "}
                        <Link to={`/tickets/${t.id}`} className="text-brand-500 hover:underline">
                          #{t.ticket_number}
                        </Link>
                      </span>
                    ))}
                    {g.source_tickets.length > 3 && ` +${g.source_tickets.length - 3}`}
                  </p>
                )}
              </div>
              {g.status !== "resolved" && (
                <div className="flex gap-1 shrink-0">
                  {g.status === "open" && (
                    <button
                      onClick={() => statusMutation.mutate({ id: g.id, status: "acknowledged" })}
                      disabled={statusMutation.isPending && statusMutation.variables?.id === g.id}
                      className="text-xs px-2 py-1 rounded border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-40"
                    >
                      ידוע
                    </button>
                  )}
                  {articleCreatedFor === g.id ? (
                    <Link
                      to="/knowledge"
                      className="text-xs px-2 py-1 rounded border border-green-300 text-green-700 bg-green-50 hover:bg-green-100"
                    >
                      ✓ ערוך מאמר ←
                    </Link>
                  ) : (
                    <button
                      onClick={() => createArticleMutation.mutate(g.id)}
                      disabled={createArticleMutation.isPending && createArticleMutation.variables === g.id}
                      className="text-xs px-2 py-1 rounded border border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-40"
                      title="צור טיוטת מאמר מידע מפער זה"
                    >
                      📝
                    </button>
                  )}
                  <button
                    onClick={() => statusMutation.mutate({ id: g.id, status: "resolved" })}
                    disabled={statusMutation.isPending && statusMutation.variables?.id === g.id}
                    className="text-xs px-2 py-1 rounded border border-green-300 text-green-700 hover:bg-green-50 disabled:opacity-40"
                  >
                    נפתר
                  </button>
                </div>
              )}
            </div>
          ))}
          {gaps.length > 5 && (
            <button
              onClick={() => setShowAll((v) => !v)}
              className="text-xs text-brand-600 hover:underline w-full text-center pt-1"
            >
              {showAll ? "הצג פחות" : `הצג עוד ${gaps.length - 5}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── ConnectionsSection ───────────────────────────────────────────────────────

function ConnectionsSection({ data, failed, stale, since }) {
  if (!data) {
    return failed ? (
      <SectionCard title="חיבורים" icon="🔌">
        <div role="alert" className="text-sm text-gray-600">לא ניתן לטעון את מצב החיבורים</div>
      </SectionCard>
    ) : null;
  }

  const toolRegistry = data.tool_registry || [];
  const waitingForNode = toolRegistry.filter((tool) => tool.status === "requires_connected_node");
  const checks = [
    { label: "OpenClaw Runtime",     ok: data.openclaw?.configured,    sub: data.openclaw?.mode_label },
    { label: "TeamViewer",           ok: data.teamviewer?.configured,  sub: data.teamviewer?.configured ? "Token מוגדר" : "לא מוגדר" },
    { label: "Microsoft 365/Graph",  ok: data.ms365?.configured,       sub: data.ms365?.mailbox },
    { label: "AI Analysis",          ok: data.ai_analysis?.configured, sub: data.ai_analysis?.model || "לא מוגדר" },
  ];

  return (
    <SectionCard title="חיבורים" icon="🔌">
      <SourceStaleLabel stale={stale} since={since} />
      <div className="space-y-2">
        {checks.map((c) => (
          <HealthIndicator key={c.label} ok={c.ok} label={c.label} sublabel={c.sub} />
        ))}
        {toolRegistry.length > 0 && (
          <HealthIndicator
            ok={waitingForNode.length === 0 ? true : null}
            label="Tool Registry"
            sublabel={waitingForNode.length > 0
              ? `${toolRegistry.length} כלים מוגדרים; ממתינים לצומת OpenClaw מחובר`
              : `${toolRegistry.length} כלים מוגדרים וזמינים`}
          />
        )}
      </div>
      <Link to="/connections" className="text-xs text-brand-600 hover:underline">
        פרטים ובדיקת חיבור ←
      </Link>
    </SectionCard>
  );
}

// ─── AgentRunsSection ─────────────────────────────────────────────────────────

// `runs` is only the WINDOW newest runs; the waiting-for-review count comes from the aggregate stats (`pendingReviewTotal`), otherwise an
// old run still awaiting review read as "אין ממתינים" as soon as 20 newer runs existed. Failures and active runs are labelled as the window.
const RUNS_WINDOW = 20;

function AgentRunsSection({ runs, loadFailed, pendingReviewTotal, stale, since }) {
  if (!runs) {
    return loadFailed ? (
      <SectionCard title="ריצות AI" icon="⚡">
        <div role="alert" className="text-sm text-gray-600">לא ניתן לטעון את ריצות ה-AI</div>
      </SectionCard>
    ) : null;
  }

  const active    = runs.filter((r) => ["pending","analyzing","diagnosing","connection_active","executing"].includes(r.status));
  const review    = runs.filter((r) => ["awaiting_technician_review","awaiting_approval"].includes(r.status));
  const failed    = runs.filter((r) => r.status === "failed");
  const reviewCount = Number.isFinite(pendingReviewTotal) ? pendingReviewTotal : review.length;
  const healthy   = active.length === 0 && reviewCount === 0 && failed.length === 0;

  return (
    <SectionCard title="ריצות AI" icon="⚡">
      <SourceStaleLabel stale={stale} since={since} />
      <div className="space-y-2">
        <HealthIndicator
          ok={reviewCount === 0}
          label="ממתינים לאישור"
          sublabel={reviewCount === 0 ? "אין ממתינים" : `${reviewCount} ריצות ממתינות`}
        />
        <HealthIndicator
          ok={active.length === 0 ? null : true}
          label="ריצות פעילות"
          sublabel={active.length === 0 ? "אין ריצות פעילות כעת" : `${active.length} פעילות`}
        />
        <HealthIndicator
          ok={failed.length === 0}
          label={`כשלים (${RUNS_WINDOW} ריצות אחרונות)`}
          sublabel={failed.length === 0 ? "אין כשלים" : `${failed.length} ריצות נכשלו`}
        />
      </div>
      {!healthy && (
        <Link to="/agent-runs" className="text-xs text-brand-600 hover:underline">
          לוח ריצות ←
        </Link>
      )}
    </SectionCard>
  );
}

// ─── PlatformHealthPage ───────────────────────────────────────────────────────

export default function PlatformHealthPage() {
  const qc = useQueryClient();
  const { addNotification } = useNotification();
  const isOnline = useOnlineStatus();

  const serverQuery = useQuery({
    queryKey: ["server-status"],
    queryFn: () => serverApi.status(),
    refetchOnWindowFocus: false,
  });
  const { data: serverData, isLoading: serverLoading, isError: serverFailed } = serverQuery;

  const connQuery = useQuery({
    queryKey: ["connectionsHealth"],
    queryFn: () => connectionsApi.health(),
  });
  const { data: connData, isLoading: connLoading, isError: connFailed } = connQuery;

  const runsQuery = useQuery({
    queryKey: ["agentRuns", ""],
    queryFn: () => agentRunsApi.listAll({ limit: RUNS_WINDOW }),
    refetchInterval: 30000,
  });
  const { data: runsData, isLoading: runsLoading, isError: runsFailed } = runsQuery;
  const { data: runsStats } = useQuery({
    queryKey: ["agentRunsStats", "health"],
    queryFn: () => agentRunsApi.stats(),
    refetchInterval: 30000,
  });

  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await serverApi.refresh();
      await qc.invalidateQueries({ queryKey: ["server-status"] });
    } catch (err) {
      addNotification(err?.response?.data?.detail || "שגיאה ברענון נתוני השרת", "error");
    } finally {
      setRefreshing(false);
    }
  };

  const isLoading = serverLoading || connLoading || runsLoading;

  // Two independently evidenced feeds: the server status is read on mount/manual refresh only
  // (CACHED is the honest label), while the run feed polls every 30s.
  const serverTruth = classifySourceTruth(serverQuery, { online: isOnline });
  const runsTruth = classifySourceTruth(runsQuery, { online: isOnline, pollingMs: 30_000 });

  return (
    <div className="space-y-5 max-w-5xl mx-auto" dir="rtl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">בריאות פלטפורמה</h1>
          <p className="text-sm text-gray-500 mt-0.5">מצב שרת, גיבויים, חיבורים וסוכני AI</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span>שרת</span>
          <SourceTruthBadge truth={serverTruth} testId="platform-server-truth" />
          <span>ריצות</span>
          <SourceTruthBadge truth={runsTruth} testId="platform-runs-truth" />
        </div>
      </div>

      {!isOnline && (
        <p role="status" className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-sm p-3">
          {serverData != null || connData != null || runsData != null
            ? "אין חיבור לרשת — נתוני בריאות הפלטפורמה לא מתרעננים כרגע; מוצג הנתון מהטעינות האחרונות שהצליחו."
            : "אין חיבור לרשת — לא ניתן לטעון את נתוני בריאות הפלטפורמה כרגע."}
        </p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <OperationsPulseSection />
          <ServerSection
            data={serverData}
            failed={serverFailed}
            stale={isSourceStale(serverQuery)}
            since={serverQuery.dataUpdatedAt}
            onRefresh={handleRefresh}
            refreshing={refreshing}
          />
          <BackupSection backups={serverData?.backups} />
          <BackupGuardianSection />
          <PlatformGuardianSection />
          <PostureFindingsSection />
          <KnowledgeGapsSection />
          <ConnectionsSection
            data={connData}
            failed={connFailed}
            stale={isSourceStale(connQuery)}
            since={connQuery.dataUpdatedAt}
          />
          <AgentRunsSection
            runs={runsData}
            loadFailed={runsFailed}
            pendingReviewTotal={runsStats?.pending_review}
            stale={isSourceStale(runsQuery)}
            since={runsQuery.dataUpdatedAt}
          />
        </div>
      )}
    </div>
  );
}
